import styled, { keyframes, css } from "styled-components";
import { Button } from "components/ui/button";

const fadeInFromBottom = keyframes`
  0% { opacity: 0; transform: translateY(30px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const fadeOutLeft = keyframes`
  0% { opacity: 1; transform: translateX(0); }
  100% { opacity: 0; transform: translateX(-50px); }
`;

const slideInRight = keyframes`
  0% { opacity: 0; transform: translateX(40px); }
  100% { opacity: 1; transform: translateX(0); }
`;

export const AnimatedCard = styled.div`
  width: 100%;
  max-width: 700px;
  border-radius: 10px;
  padding: 30px;
  min-height: 400px;
  margin-top: 20px;
  flex-direction: column;
  position: relative;
  will-change: transform, opacity;
  transform: translateZ(0);
  animation: ${({ isFadingOut }) =>
    isFadingOut
      ? css`${fadeOutLeft} 0.32s cubic-bezier(0.22, 1, 0.36, 1) forwards`
      : css`${slideInRight} 0.32s cubic-bezier(0.23, 1, 0.32, 1) forwards`};
`;

export const TopBanner = styled.div`
  background-color: ${({ themeColors }) => themeColors.topBanner};
  width: 100%;
  height: 75px;
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
`;

export const Container = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  font-family: 'Raleway', sans-serif;
  justify-content: center;
  position: relative;
  background-color: ${({ themeColors }) => themeColors.backgroundColor};
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 700px;
  width: 100%;
`;

export const Progress = styled.div`
  font-size: 12px;
  color: #2a3039;
  margin-bottom: 5px;
  position: absolute;
  top: 10px;
  right: 50%;
`;

export const BottomBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 1000;
  width: 100vw;
  pointer-events: none;
`;

export const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  pointer-events: auto;
`;

export const ProgressWrapper = styled.div`
  text-align: center;
`;

export const BackButton = styled(Button)`
  padding: 10px 20px;
  font-size: 1rem;
  color: ${({ themeColors }) => themeColors.buttonColor};
  background-color: ${({ themeColors }) => themeColors.secondaryButton};
  border: none;
  border-radius: 4px;
  cursor: pointer;
`;

export const NextButton = styled(Button)`
  padding: 10px 20px;
  font-size: 1rem;
  color: ${({ themeColors, active }) => (active ? '#fff' : themeColors.buttonColor)};
  background-color: ${({ themeColors, active }) => (active ? themeColors.buttonColor : themeColors.secondaryButton)};
  border: none;
  border-radius: 4px;
  cursor: pointer;
`;

// Chat input grow animation
const growHorizontal = keyframes`
  0% { width: 140px; opacity: 0; }
  100% { width: 100%; opacity: 1; }
`;

const collapseHorizontal = keyframes`
  0% { width: 100%; opacity: 1; }
  100% { width: 140px; opacity: 0; }
`;

export const ChatBarContainer = styled.div`
  position: fixed;
  left: 50%;
  bottom: 14px;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 140px;
  max-width: 720px;
  padding: 8px;
  z-index: 1100;
  pointer-events: auto;
  animation: ${({ isLeaving }) => isLeaving
    ? css`${collapseHorizontal} 260ms ease-in forwards`
    : css`${growHorizontal} 420ms ease-out forwards`};
`;

export const ChatBarInner = styled.div`
  position: relative;
  width: 100%;
`;

export const ChatInputField = styled.input`
  width: 100%;
  height: 52px;
  padding: 12px 52px 12px 16px;
  border-radius: 26px;
  border: 1px solid #e5e7eb;
  outline: none;
  background: #ffffff;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  font-size: 15px;
`;

export const SendBtn = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  height: 28px;
  width: 28px;
  border-radius: 14px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ themeColors }) => themeColors.buttonColor};
  color: #ffffff;
`;

// Button shrink/grow wrappers
const shrinkToDot = keyframes`
  0% { transform: scale(1); opacity: 1; }
  80% { transform: scale(0.1); opacity: 0.2; }
  100% { transform: scale(0); opacity: 0; }
`;

const expandFromDot = keyframes`
  0% { transform: scale(0); opacity: 0; }
  60% { transform: scale(1.06); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

export const NextButtonWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${(props) => props.isShrinking && css`animation: ${shrinkToDot} 260ms ease-in forwards;`}
`;

export const GrowButtonWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  will-change: transform, opacity;
  animation: ${expandFromDot} 280ms ease-out both;
`;

// Action card pieces
export const ActionCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px;
  background: #ffffff;
`;

export const ActionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: oklch(.145 0 0);
`;

export const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`;

export const Label = styled.label`
  font-size: 13px;
  color: #374151;
`;

export const DateInput = styled.input`
  height: 36px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 6px 10px;
`;

export const SelectInput = styled.select`
  height: 36px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 6px 10px;
  background: #ffffff;
`;

// Inputs for action editing
export const TextInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  outline: none;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.03);
  font-size: 14px;
  font-family: 'Lora', serif;
`;

export const TextArea = styled.textarea`
  width: 100%;
  min-height: 140px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  outline: none;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.03);
  font-size: 14px;
  resize: vertical;
  font-family: 'Lora', serif;
`;

export const ActionCardFooter = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

export const ConfirmButton = styled(Button)`
  padding: 8px 14px;
  font-size: 14px;
  color: #fff;
  background-color: ${({ themeColors }) => themeColors.buttonColor};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

// Nora chat drawer UI
export const NoraAskButton = styled(Button)`
  padding: 8px 12px;
  font-size: 14px;
  color: #fff;
  background-color: ${({ themeColors }) => themeColors.buttonColor};
  border: none;
  border-radius: 8px;
  cursor: pointer;
`;

export const NoraDrawer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 600px;
  max-width: 96vw;
  background: #ffffff;
  border-left: 1px solid #e5e7eb;
  box-shadow: -12px 0 32px rgba(0,0,0,0.12);
  z-index: 12000;
  transform: translateX(${({ isOpen }) => (isOpen ? '0' : '100%')});
  transition: transform 280ms cubic-bezier(0.22, 1, 0.36, 1);
  display: flex;
  flex-direction: column;
`;

export const NoraOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.35);
  z-index: 11990;
`;

export const NoraHeader = styled.div`
  min-height: 56px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e5e7eb;
`;

export const NoraTitle = styled.div`
  font-weight: 600;
  color: oklch(.145 0 0);
  font-size: 16px;
`;

export const NoraHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

export const NoraSubtitle = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #6b7280;
`;

export const NoraClose = styled.button`
  height: 32px;
  width: 32px;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
`;

export const NoraMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background: #ffffff;
  font-family: 'Lora', serif;
`;

export const NoraInputBar = styled.div`
  padding: 12px;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const NoraTextField = styled.textarea`
  flex: 1;
  height: 44px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  outline: none;
  font-size: 14px;
  resize: none;
  background: #fff;
`;

export const NoraSendButton = styled(Button)`
  height: 44px;
  padding: 0 14px;
  font-size: 14px;
  color: #fff;
  background-color: ${({ themeColors }) => themeColors.buttonColor};
  border: none;
  border-radius: 10px;
  cursor: pointer;
`;

export const NoraMessageRow = styled.div`
  display: flex;
  margin-bottom: 10px;
  justify-content: ${({ role }) => (role === 'user' ? 'flex-end' : 'flex-start')};
`;

export const NoraMessageBubble = styled.div`
  width: ${({ role }) => (role === 'assistant' ? '100%' : 'auto')};
  max-width: ${({ role }) => (role === 'assistant' ? '100%' : '80%')};
  padding: ${({ role }) => (role === 'assistant' ? '0' : '8px 12px')};
  border-radius: ${({ role }) => (role === 'assistant' ? '0' : '12px')};
  font-size: 14px;
  line-height: 1.6;
  color: #111827;
  background: ${({ role }) => (role === 'user' ? '#f3f4f6' : 'transparent')};
  border: ${({ role }) => (role === 'user' ? '1px solid #e5e7eb' : 'none')};
`;

export const NoraTimestamp = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-top: 6px;
`;

const typingPulse = keyframes`
  0% { opacity: 0.2; }
  50% { opacity: 1; }
  100% { opacity: 0.2; }
`;

export const TypingDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  background: #111827;
  border-radius: 50%;
  margin-left: 6px;
  animation: ${typingPulse} 1s ease-in-out infinite;
`;

// Simple loading spinner
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const LoadingSpinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid #e5e7eb;
  border-top-color: #111827;
  border-radius: 50%;
  animation: ${spin} 0.9s linear infinite;
`;

// Skeleton bar with pulse
const pulse = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

export const SkeletonBar = styled.div`
  height: ${({ height }) => height || '12px'};
  width: ${({ width }) => width || '100%'};
  background: #e5e7eb;
  border-radius: 6px;
  animation: ${pulse} 1.2s ease-in-out infinite;
`;

// Subtle reveal for newly generated recommendations
const revealSoft = keyframes`
  0% { opacity: 0; transform: translateY(6px) scale(0.995); filter: blur(6px); }
  60% { opacity: 1; transform: translateY(0) scale(1.005); filter: blur(0); }
  100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
`;

export const RecommendationReveal = styled.div`
  will-change: opacity, transform, filter;
  animation: ${revealSoft} 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
`;
